import isEmpty from 'lodash/isEmpty';
import * as PIXI from 'pixi.js';
import * as Sentry from '@sentry/browser';
import type { DestroyOptions } from '../../types';
import { FETCH_ON_BUFFER } from '../../types';
import { AudioAsset } from '../../audio/AudioAsset';
import { maybeProxy } from '../../libs/other';
import { roundToPrecision } from '../../libs/frameUtils';
import { TransformMatrix } from '../../libs/TransformMatrix';
import { getMidPointFromRect } from '../../libs/padding';
import {
    BufferingAbortedError,
    FailedToLoadError
} from '../../errors';

import {
    assertInArray,
    assertBoolean,
    assertNumber,
    assertInEnum,
    assertMinimum,
    assertIsValidMediaSourceData
} from '../../validators';
import { Config } from '../../video-data/config';
import { LinearCurve } from '../../libs/LinearCurve';
import { getGlobalAssetNetworkManager } from '../AssetNetworkManager';
import { getPositionFromTransformation } from '../../video-data/designs/scenes';
import type { LicenseEnum } from '../../enums';
import { BufferingMode, DynamicStylingCreationLevel, CreationType } from '../../enums';
import { BooleanAttribute, getAttributesToSerialize } from '../attributes';
import type {
    AdjustmentFilter,
    BlurFilter,
    HslAdjustmentFilter,
    OldFilmFilter
} from '../filters';
import { StencilMask } from '../mask/StencilMask';
import type { FillMode } from '../../video-data/types';
import type { CompositingAsset } from '../CompositingAsset';
import {
    calculateScaledResolution,
    setTransform,
    resetTransform
} from './mediaUtils';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';
import type { TimeAwareMediaAsset } from './TimeAwareMediaAsset';

type MediaAssetBase64DataType = {
    base64: string;
    width: number;
    height: number;
    format: string;
};

class MediaAssetBase64Data {
    base64: string;
    width: number;
    height: number;
    format: string;

    constructor({ base64, width, height, format }: MediaAssetBase64DataType) {
        this.base64 = base64;
        this.width = width;
        this.height = height;
        this.format = format;
    }

    /** strips away the data URL prefix from the base 64 string. This is necessary if we want to directly write it to disk. */
    withoutDataURLPrefix() {
        return this.base64.replace(`data:${this.format};base64,`, '');
    }
}

type TranslationLimit = {
    /**
     *  txLimit - max value of transformMatrix.tx
     */
    x: [number, number];
    /**
     * tyLimit - max value of transformMatrix.ty
     */
    y: [number, number];
};

class VideoResource extends PIXI.VideoResource {
    override upload(renderer, baseTexture, glTexture, source) {
        if (global && global.NODE) {
            return super.upload(renderer, baseTexture, glTexture, source);
        }
        const gl = renderer.gl;
        const width = baseTexture.realWidth;
        const height = baseTexture.realHeight;
        source = source || this.source;

        if (typeof HTMLImageElement !== 'undefined' && source instanceof HTMLImageElement) {
            if (!source.complete || source.naturalWidth === 0) {
                return false;
            }
        }
        else if (typeof HTMLVideoElement !== 'undefined' && source instanceof HTMLVideoElement) {
            if (source.readyState <= 1 && source.buffered.length === 0) {
                return false;
            }
        }

        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, baseTexture.alphaMode === PIXI.ALPHA_MODES.UNPACK);

        if (!this.noSubImage
            && baseTexture.target === gl.TEXTURE_2D
            && glTexture.width === width
            && glTexture.height === height) {
            gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, baseTexture.format, glTexture.type, source);
        }
        else {
            glTexture.width = width;
            glTexture.height = height;

            gl.texImage2D(baseTexture.target, 0, glTexture.internalFormat, baseTexture.format, glTexture.type, source);
        }

        return true;
    }
}

export type MediaAssetSettings = BaseAssetSettings & {
    shouldRenderResizedImage?: boolean;
    shouldUseMipMapping?: boolean;
    fillMode?: FillMode;
};

export type MediaSourceData = {
    uuid: string;
    type: string;
    source: number;
    source_id: string;
    license: LicenseEnum;
    is_editorial: boolean;
    width: number;
    height: number;
    thumbnail_url: string;
    preview_url: string;
    render_url: string | null; // allowed to be null for Shutterstock, Getty, user uploads
    title: string | null;
    // properties only on VideoAssets
    poster_url?: string;
    duration?: number;
    has_audio?: boolean | null;
    has_voiceover?: boolean | null;
    ai_avatar?: number | null;
};

/**
 * A MediaAsset Object will load the specified media file. This can be either an image or video.
 *
 * EVENTS MEDIA ASSET SENDS
 * - assetCanPlay:  MediaAsset is now playable.  Typically this means that the asset has loaded but also
 *                  may be sent even if the asset hasn't loaded yet.  This behaviour is to ensure we don't
 *                  lock up workspace playback when a given asset takes longer then expected to load.  Emitted
 *                  on the SLIDE.
 */
export abstract class MediaAsset extends BaseAsset {
    override _classType = 'MediaAsset';
    static ADJUSTMENT_FILTER_NAME = 'AdjustmentFilter_1';
    static BLUR_FILTER_NAME = 'BlurFilter_1';
    static GREYSCALE_FILTER_NAME = 'GreyscaleFilter_1';
    static HSL_ADJUSTMENT_FILTER_NAME = 'HslAdjustmentFilter_1';
    static OLD_FILM_FILTER_NAME = 'OldFilmFilter_1';

    #kenburn = Config.NONE_BACKGROUND_ANIMATION;
    #maxWidth: number = undefined;
    #maxHeight: number = undefined;
    #scaleMultiplier = 1;

    #license = Config.CC0_LICENSE;
    #isEditorial = false;
    #sourceDuration: number = null;
    #hasAudio: boolean | null = null;
    #hasVoiceover: boolean | null = null;
    #mediaSource: number = undefined;
    #mediaSourceId: string = undefined;
    #mediaId: string = undefined;
    #title: string = null;
    #thumbnailUrl: string = undefined;
    #previewUrl: string = undefined;
    #renderUrl: string | null = null;
    #posterUrl: string = undefined;
    #aiAvatar = null;

    #sourceRenderWidth: number = null;
    #sourceRenderHeight: number = null;

    #mediaTargetResolution = { width: 512, height: 512 };
    #transformMatrix: TransformMatrix = new (TransformMatrix as any)({ parent: this, redrawParentOnUpdate: true });

    #sprite: PIXI.Sprite = null;
    #originalTexture: PIXI.Texture = null;
    #resource: PIXI.ImageResource | VideoResource = null;

    #audioAsset: AudioAsset;

    #failed = false;
    #loadStatusInterval = null;
    #loadStatusMaxCount: number = this.root.settings.loadStatusMaxCount;
    #loadStatusCount = 0;
    #onBufferingTimeoutFallback = null;
    #abortBuffering: (error: BufferingAbortedError) => void = () => {};

    // media source fields
    #fillMode: FillMode = Config.FILL_COVER;

    /**
     * Resize image to fit target resolution to avoid flickering when animating. This property should not be used in
     * tandem with shouldUseMipMapping.
     */
    #shouldRenderResizedImage = true;

    /**
     * Use mip mapping to fit target resolution for a softer result. This property should not be used in tandem with
     * shouldRenderResizedImage.
     */
    #shouldUseMipMapping = false;

    // TODO: unify resizing options to a tri-state: none, resizeImage or mipMapping (LU-885)

    #sourceUrl: string = null;
    /**
     * Source URL of the currently loaded (or loading) media
     */
    #loadingSourceUrl: string = null;
    #sourceMimeType = null;
    #sourceType: typeof Config.ALL_BACKGROUND_TYPES[0] = null;

    @BooleanAttribute()
    linkToSceneGroup = false;
    @BooleanAttribute()
    flipHorizontally = false;
    @BooleanAttribute()
    flipVertically = false;

    #linkedMediaAsset: TimeAwareMediaAsset | CompositingAsset = null;

    /**
     * The container used by kenburns to scale the media. These scale values may be animated over time.
     */
    protected _kenburnsScaleContainer = new PIXI.Container();
    /**
     * The container used for static scaling and rotating (do not change as time progresses),
     * these are the values that the user will specify.
     */
    #scaleRotateContainer = new PIXI.Container();
    /**
     * The container used for dynamic animations (these are set up in the scene design and
     * will change as time progresses).
     */
    #dynamicAnimationContainer = new PIXI.Container();

    /**
     * @param container - the container we want to house this asset under
     * @param settings - configuration object
     */
    constructor(container, settings: MediaAssetSettings) {
        super(container, settings);

        this._kenburnsScaleContainer.name = 'kenburnsScaleContainer';
        this.#scaleRotateContainer.name = 'scaleRotateContainer';
        this.#dynamicAnimationContainer.name = 'dynamicAnimationContainer';

        /**
         * The order of the containers matters. Transformations are applied bottom up, so we want to organize the
         * transformations so that translating happens last. We want scale & rotations to happen first, so that we maintain
         * the same origin for scale & rotate regardless of the translation. This is how the current hierarchy is set up:
         * ->    MediaAsset (this)
         *        - this is where translations are applied. All static translations are set up on this Container, but
         *          dynamic animations still happen on the lower container (which we may want to change eventually)
         *        - kenburns applies its animated translations here too, but the animation accounts for the static
         *          translations applied before it
         *        - masks are applied at this level
         * -->   kenburnsScaleContainer
         *        - this container should only be used for kenburns scaling
         *        - we want this above the scaleRotateContainer so that the kenburns always happen in the x & y direction
         *          of the scene. If the asset is rotated by the user we don't want the kenburns to rotate with it.
         *        - since we move the pivot, we need to be careful not to set x/y values directly (offsetting is okay)
         * --->  scaleRotateContainer
         *        - this container is used for static flipping & rotating (done by the user)
         *        - since we move the pivot, we need to be careful not to set x/y values directly (offsetting is okay)
         *        - dynamic graphics are also attached to this container, so that they flip & rotate with the media
         * ----> dynamicAnimationContainer
         *        - this container is used by dynamic styling animations
         *        - we use a separate container so it doesn't conflict with user edits or kenburns (lets them stack)
         *        - since we move the pivot, we need to be careful not to set x/y values directly (offsetting is okay)
         *        - technically if we have position animations that do not offset (i.e. are absolute), they won't work
         *          as expected (they should be done at the top level with the other translations), but we don't have
         *          that use case right now for media asset so this is fine
         *        - the media sprite is attached to this container
         *        - filters are applied at this level
         */
        this._addInternalChild(this._kenburnsScaleContainer);
        this._kenburnsScaleContainer.addChild(this.#scaleRotateContainer);
        this.#scaleRotateContainer.addChild(this.#dynamicAnimationContainer);

        // resize image properties
        this.fillMode = settings.fillMode || Config.FILL_COVER;
        this.shouldRenderResizedImage = settings.hasOwnProperty('shouldRenderResizedImage') ? settings.shouldRenderResizedImage : true;
        this.shouldUseMipMapping = settings.hasOwnProperty('shouldUseMipMapping') ? settings.shouldUseMipMapping : false;
        this.#mediaTargetResolution = Object.assign({}, this.root.referenceResolution);
        this.#maxWidth = this.#mediaTargetResolution.width;
        this.#maxHeight = this.#mediaTargetResolution.height;
        this.#loadStatusMaxCount = this.root.settings.loadStatusMaxCount;

        // events
        this.on('onLoaded', this.onLoaded, this);

        // audio
        this.#audioAsset = new AudioAsset({
            fetchResources: settings.fetchResources,
            name: `audio_${this.name}`,
            redrawParentOnUpdate: true,
            parent: this,
            autoUpdate: true, // ?? should we connect this to autoDraw ??
        });
    }

    /**
     * We want to separate the filters from the masks, so that masks will also clip filters.
     * This means we apply the filter on a lower container while the mask is applied on a higher container.
     */
    override get _pixiFilterContainer(): PIXI.DisplayObject {
        // We use the bottom container not only so that masks clip filters, but also so that
        // filters don't apply to any dynamic graphics.
        return this.#dynamicAnimationContainer;
    }

    /**
     * Emits the onLoaded event indicating the asset has loaded.
     */
    #emitSourceLoaded = () => {
        this.emit('onLoaded');
        if (this.canPlay) {
            this._emitAssetCanPlay();
        }
    };

    /**
     * Used to internally communicate that a load attempt failed. We may still retry loading.
     */
    #loadAttemptFailed(reason: string) {
        console.warn(`Load attempt failed because: ${reason}`);
        this.#failed = true;
        this.emit('onLoadAttemptFailed');
    }

    /**
     * Emits the onBufferingDone event indicating that the asset finished loading
     * the content for the requested time.
     * @internal
     */
    _onSourceBufferingDone = () => {
        this.emit('onBufferingDone');
    };

    /**
     * The original texture. This is NOT necessarily the texture that is rendered as we create a new texture on the fly
     * when we have to resize the image.
     * @internal
     */
    get _originalTexture(): PIXI.Texture {
        return this.#originalTexture;
    }

    /**
     * The sprite which contains the texture we render
     * @internal
     */
    get _sprite() {
        return this.#sprite;
    }

    get _displayTexture() {
        if (!this.#sprite) {
            throw new Error('Sprite is not yet created');
        }
        return this.#sprite.texture;
    }

    get mediaTargetResolution() {
        return this.#mediaTargetResolution;
    }

    set fillMode(value) {
        if (value === this.fillMode) {
            return;
        }
        assertInArray(value, Config.ALL_FILL_MODES);
        this.#fillMode = value;
        this.requiresRedraw = true;
    }

    get fillMode() {
        return this.#fillMode;
    }

    /**
     * Determine if we're scaling the image to match the base resolution a close as possible
     */
    get shouldRenderResizedImage(): boolean {
        return this.#shouldRenderResizedImage;
    }

    set shouldRenderResizedImage(value) {
        if (this.shouldRenderResizedImage === value) {
            return;
        }
        assertBoolean(value);
        if (value && this.shouldUseMipMapping) {
            this.shouldUseMipMapping = false;
        }
        this.#shouldRenderResizedImage = value;
        // in all cases, require a new redraw (either to draw or just to trigger a state change)
        this.requiresRedraw = true;
    }

    /**
     * Determine if we use WebGL to scale and filter the texture (known as mip mapping) into multiple resolutions which
     * results in a softer, slightly blurry texture.
     */
    get shouldUseMipMapping(): boolean {
        return this.#shouldUseMipMapping;
    }

    set shouldUseMipMapping(value: boolean) {
        if (value === this.shouldUseMipMapping) {
            return;
        }
        assertBoolean(value);
        if (value && this.shouldRenderResizedImage) {
            this.shouldRenderResizedImage = false;
        }
        this.#shouldUseMipMapping = value;
        // in all cases, require a new redraw (either to draw or just to trigger a state change)
        this.requiresRedraw = true;
    }

    get mediaId(): string {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.mediaId;
        }
        return this.#mediaId;
    }

    get title(): string {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.title;
        }
        return this.#title;
    }

    get mediaSourceId(): string {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.mediaSourceId;
        }
        return this.#mediaSourceId;
    }

    get license(): string {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.license;
        }
        return this.#license;
    }

    get isEditorial(): boolean {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.isEditorial;
        }
        return this.#isEditorial;
    }

    get mediaSource(): number {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.mediaSource;
        }
        return this.#mediaSource;
    }

    /**
     * Returns us the loaded media type. This can be any of Config.ALL_BACKGROUND_TYPES.
     */
    get mediaAssetType(): typeof Config.ALL_BACKGROUND_TYPES[0] {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.mediaAssetType;
        }
        return this.#sourceType;
    }

    get sourceRenderWidth(): number {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.sourceRenderWidth;
        }
        return this.#sourceRenderWidth;
    }

    get sourceRenderHeight(): number {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.sourceRenderHeight;
        }
        return this.#sourceRenderHeight;
    }

    /**
     * The kenburn or ken burns effect is an animation preset which gets applied on the overall media element. The kenburn
     * string value gets converted into a number of animation curves which drive the necessary properties during
     * `applyAnimation` to achieve the necessary effect such as `zoom_in` or `pan_left`.
     */
    get kenburn(): string {
        return this.#kenburn;
    }

    set kenburn(value) {
        if (this.kenburn === value) {
            return;
        }
        assertInEnum(value, Config.ALL_BACKGROUND_ANIMATIONS);
        this.#kenburn = value;
        this.requiresRedraw = true;
    }

    /**
     * The maximum width of the media asset.
     */
    get maxWidth(): number {
        return this.#maxWidth;
    }

    set maxWidth(value: number) {
        if (this.maxWidth === value) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#maxWidth = value;
        this.requiresRedraw = true;
    }

    /**
     * The maximum height of the media asset.
     */
    get maxHeight(): number {
        return this.#maxHeight;
    }

    set maxHeight(value: number) {
        if (this.maxHeight === value) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#maxHeight = value;
        this.requiresRedraw = true;
    }

    /**
     * Returns the approximate biggest possible bounds. For media assets with fillmode = cover this should be pretty
     * accurate to the bounds it'll take up. For media assets with other fillmodes their size depends on their
     * source dimensions and this method will return the maximum dimensions.
     *
     * If there is a mask, then the bounds of the mask is returned.
     *
     * @returns { top, height, left, width }. Each property is a number in the same units
     *  as luminary's resolution
     */
    override get approximateMaxBounds(): PIXI.Rectangle {
        if (this.mask instanceof StencilMask) {
            return this.mask.getMaxBounds();
        }

        let width = this.maxWidth;
        let height = this.maxHeight;

        // if we have source data set then account for the fill mode
        if (this.isSourceDataSet && this.#sourceRenderWidth && this.#sourceRenderHeight) {
            const fillModeResult = calculateScaledResolution(
                { width: this.#sourceRenderWidth, height: this.#sourceRenderHeight },
                { width, height },
                this.fillMode
            );
            width = fillModeResult.width;
            height = fillModeResult.height;
        }

        // get the transformation for the asset that controls our position
        const transformation = this.getFreelyPositionedParent().transformation;
        const { x, y } = getPositionFromTransformation(transformation, width, height);

        return new PIXI.Rectangle(x, y, width, height);
    }

    /**
     * Some layouts, such as ImageMoveLarge animate in from outside the final mask area and
     * therefore need extra 'overscan' image information. A scale multiplier can be set
     * specifically for the few layouts requiring this special treatment. If the assigned scale
     * multiplier value seems a bit high, it's because of the sizing required for zoom-style
     * kenburns animation.
     *
     * WARNING: Avoid using this whenever possible as it results in the final cropping looking
     * different than what was defined by the user.
     */
    get scaleMultiplier(): number {
        return this.#scaleMultiplier;
    }

    set scaleMultiplier(value: number) {
        if (this.scaleMultiplier === value) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#scaleMultiplier = value;
        this.requiresRedraw = true;
    }

    /**
     * Update the currently active source (this.#sourceUrl)
     */
    _updateSourceOnMediaTag() {
        if (this.textureSourceDomElement) {
            if (this.isVideo) {
                const videoElement = <HTMLVideoElement> this.textureSourceDomElement;
                const source = document.createElement('source');
                source.src = this.#sourceUrl;
                source.type = this.#sourceMimeType;

                if (videoElement.children.length === 1 &&
                    videoElement.children[0].outerHTML === source.outerHTML) {
                    // Source is already up to date; skip!
                    return;
                }

                (videoElement.children[0] as HTMLSourceElement).src = '';
                videoElement.removeChild(videoElement.children[0]);

                videoElement.appendChild(source);
                videoElement.load();
            }
            else {
                const imageElement = <HTMLImageElement> this.textureSourceDomElement;
                imageElement.src = this.#sourceUrl;
            }
        }
    }

    /**
     * Returns the linked media asset. This asset will only be present when linkToSceneGroup is true and the linked
     * asset is loaded.
     */
    get _linkedMediaAsset(): TimeAwareMediaAsset | CompositingAsset | null {
        return this.#linkedMediaAsset;
    }

    get isLinkedToCompositingAsset(): boolean {
        if (!this.#linkedMediaAsset) {
            return false;
        }
        return Config.isCompositingAsset(this.#linkedMediaAsset as CompositingAsset);
    }

    get isLinkedToMediaAsset(): boolean {
        if (!this.#linkedMediaAsset) {
            return false;
        }
        return Config.isMediaAsset(this.#linkedMediaAsset as TimeAwareMediaAsset);
    }

    /**
     * @internal
     */
    linkSourceTo(asset: TimeAwareMediaAsset | CompositingAsset) {
        // If the base textures are the same, then we're already linked
        if (Config.isMediaAsset(asset as TimeAwareMediaAsset)) {
            if (this.#originalTexture && this.#originalTexture.baseTexture === (asset as TimeAwareMediaAsset)._originalTexture?.baseTexture) {
                return;
            }
        }
        else {
            if (this.#originalTexture && this.#originalTexture.baseTexture === (asset as CompositingAsset).baseTexture) {
                return;
            }
        }

        this.#clearExceptTransformations();

        // Link immediately rather than in #linkSourceTo, so that onSourceLoaded
        // waits for asset.onSourceLoaded, even before onLoaded fires.
        this.#linkedMediaAsset = asset;

        if (asset.isLoaded) {
            this.#linkSourceTo(asset);
        }
        else {
            asset.once('onLoaded', () => {
                if (this.destroyed) {
                    return;
                }
                if (!this.#linkedMediaAsset || this.#linkedMediaAsset !== asset) {
                    // If we get here, the asset was cleared
                    return;
                }
                this.#linkSourceTo(asset);
            });
        }
    }

    #linkSourceTo(mediaAsset: TimeAwareMediaAsset | CompositingAsset) {
        if (this.isLinkedToMediaAsset) {
            mediaAsset = mediaAsset as TimeAwareMediaAsset;
            if (!mediaAsset.shouldLoad || !mediaAsset._originalTexture) {
                return;
            }
            this.#originalTexture = new PIXI.Texture(mediaAsset._originalTexture.baseTexture);
        }
        else {
            mediaAsset = mediaAsset as CompositingAsset;
            if (!mediaAsset.shouldLoad || !mediaAsset.baseTexture) {
                return;
            }
            this.#originalTexture = new PIXI.Texture(mediaAsset.baseTexture);
        }
        this.#sprite = new PIXI.Sprite(this.#originalTexture);
        this.#dynamicAnimationContainer.removeChildren();
        this.#dynamicAnimationContainer.addChild(this.#sprite);

        this.requiresRedraw = true;

        this.#emitSourceLoaded();
    }

    /**
     * Used to compare the current source data to the new source data. This explicitly only
     * checks properties that are relevant to MediaAsset. Since this data often comes from the
     * database we don't want to consider properties that are otherwise ignored.
     */
    #isSourceDataEqual(data: MediaSourceData): boolean {
        if (isEmpty(data)) {
            return isEmpty(this.sourceData);
        }
        const checkOptional = (a: any, b: any) => {
            if (!a) {
                return !!b;
            }
            return a !== b;
        };
        if (data.uuid !== this.#mediaId) {
            return false;
        }
        if (checkOptional(data.title, this.#title)) {
            return false;
        }
        if (data.type !== this.#sourceType) {
            return false;
        }
        if (data.source !== this.#mediaSource) {
            return false;
        }
        if (data.source_id !== this.#mediaSourceId) {
            return false;
        }
        if (data.license !== this.#license) {
            return false;
        }
        if (data.is_editorial !== this.#isEditorial) {
            return false;
        }
        if (data.width !== this.#sourceRenderWidth) {
            return false;
        }
        if (data.height !== this.#sourceRenderHeight) {
            return false;
        }
        if (data.thumbnail_url !== this.#thumbnailUrl) {
            return false;
        }
        if (data.preview_url !== this.#previewUrl) {
            return false;
        }
        if (data.render_url !== this.#renderUrl) {
            return false;
        }
        if (this.isVideo) {
            if (checkOptional(data.poster_url, this.#posterUrl)) {
                return false;
            }
            if (checkOptional(data.has_audio, this.#hasAudio)) {
                return false;
            }
            if (checkOptional(data.has_voiceover, this.#hasVoiceover)) {
                return false;
            }
            // Skip checking duration because MediaAsset updates it after loading
        }
        // Any other key difference we don't care about, so return true
        return true;
    }

    /**
     * Set source data. Derived from our media asset database, the source data contains a list of media size urls plus
     * a variety of related properties. All of these properties are fixed as they are owned by the media asset database
     * which is why we don't provide any way of setting or changing the properties.
     */
    set sourceData(data: MediaSourceData) {
        if (this.#isSourceDataEqual(data)) {
            return;
        }
        // validate input
        assertIsValidMediaSourceData(data);

        if (this.isSourceDataSet) {
            // Only want to reset the transformations if we're changing the source data. For example, during
            // load, we don't want to reset the transformations otherwise we'll overwrite values being loaded.
            this.resetEditableTransformations();
        }

        // assign property values
        if (data.title) {
            this.#title = data.title;
        }
        this.#mediaId = data.uuid;
        this.#sourceType = data.type as typeof Config.ALL_BACKGROUND_TYPES[0];
        this.#mediaSource = data.source;
        this.#mediaSourceId = data.source_id;
        this.#license = data.license;
        this.#isEditorial = data.is_editorial;
        this.#sourceRenderWidth = data.width;
        this.#sourceRenderHeight = data.height;
        this.#thumbnailUrl = data.thumbnail_url;
        this.#previewUrl = data.preview_url;
        this.#renderUrl = data.render_url;

        if (data.type === Config.VIDEO_TYPE_BACKGROUND) {
            this.#posterUrl = data.poster_url;
            this.#sourceDuration = data.duration;
            this.#hasAudio = data.has_audio;
            this.#hasVoiceover = data.has_voiceover;
            this.#sourceMimeType = 'video/mp4';
            this.#aiAvatar = data?.ai_avatar;
        }
        else {
            this.#posterUrl = undefined;
            this.#sourceDuration = null;
            this.#hasAudio = false;
            this.#hasVoiceover = false;
            this.#sourceMimeType = null;
        }

        let newSourceUrl: string;
        if (this.root.mediaSizeToUse === Config.THUMBNAIL_MEDIA_SIZE && data.thumbnail_url) {
            newSourceUrl = this.thumbnailUrl;
        }
        else if (this.root.mediaSizeToUse === Config.PREVIEW_MEDIA_SIZE && data.preview_url) {
            newSourceUrl = this.previewUrl;
        }
        else if (this.root.mediaSizeToUse === Config.RENDER_MEDIA_SIZE && data.render_url) {
            newSourceUrl = this.renderUrl;
        }
        else {
            throw new Error(`No valid media sources to load for ${this.root.mediaSizeToUse} (source data: ${JSON.stringify(data)})`);
        }

        newSourceUrl = maybeProxy(newSourceUrl);

        this.#sourceUrl = newSourceUrl;

        if (this.root.audioMode === Config.AUDIO_MODES.RENDER && this.#hasAudio && !this.#linkedMediaAsset) {
            // loading is done through the audioAsset in this case
            this.#audioAsset.url = this.#sourceUrl;
        }

        this.requiresRedraw = true;
    }

    /**
     * The source data is considered set when there is a thumbnail, preview or render url.
     */
    get isSourceDataSet(): boolean {
        return !!(this.#thumbnailUrl || this.#previewUrl || this.#renderUrl);
    }

    /**
     * Get source data for a media asset.
     */
    get sourceData(): MediaSourceData {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.sourceData;
        }
        if (!this.isSourceDataSet) {
            return {} as MediaSourceData;
        }

        const data: MediaSourceData = {
            // assign property values
            uuid: this.#mediaId,
            title: this.#title,
            type: this.#sourceType,
            source: this.#mediaSource,
            source_id: this.#mediaSourceId,
            license: this.#license,
            is_editorial: this.#isEditorial,
            width: this.#sourceRenderWidth,
            height: this.#sourceRenderHeight,
            thumbnail_url: this.#thumbnailUrl,
            preview_url: this.#previewUrl,
            render_url: this.#renderUrl,
        };
        if (this.isVideo) {
            data.poster_url = this.#posterUrl;
            data.duration = this.#sourceDuration;
            data.has_audio = this.#hasAudio;
            data.has_voiceover = this.#hasVoiceover;
            data.ai_avatar = this.#aiAvatar;
        }
        return data;
    }

    /**
     * The media thumbnail url (smallest url; used in the media tab)
     */
    get thumbnailUrl(): string {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.thumbnailUrl;
        }
        return this.#thumbnailUrl;
    }

    /**
     * The media preview url (middle-sized url; used for previews in the creator)
     */
    get previewUrl(): string {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.previewUrl;
        }
        return this.#previewUrl;
    }

    /**
     * The media render url (largest url; used when rendering)
     */
    get renderUrl(): string | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.renderUrl;
        }
        return this.#renderUrl;
    }

    /**
     * A image url that represents a video asset
     */
    get posterUrl(): string | number {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.posterUrl;
        }
        return this.#posterUrl;
    }

    /**
     * The duration of the source in milliseconds.
     */
    get sourceDuration(): number {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.sourceDuration;
        }
        return this.#sourceDuration;
    }

    protected _loadSourceWithNetworkManager(ignoreShouldLoad = false) {
        if (this.#linkedMediaAsset) {
            return;
        }
        if (!this.shouldLoad && !ignoreShouldLoad) {
            return;
        }

        if (this.#loadingSourceUrl === this.#sourceUrl) {
            return;
        }

        if (this.#loadingSourceUrl !== null) {
            this.clear();
        }

        this.#loadingSourceUrl = this.#sourceUrl;

        const assetNetworkManager = getGlobalAssetNetworkManager();
        assetNetworkManager.waitForNetworkSlot(this, requestFinished => {
            this.#loadSource(requestFinished);
        });
    }

    /**
     * @internal
     */
    get _audioAsset() {
        return this.#audioAsset;
    }

    /**
     * Load the `sourceUrl` specified during `sourceData`.
     */
    #loadSource(requestFinished: () => void) {
        if (this.destroyed) {
            requestFinished();
            return;
        }

        if (this.#linkedMediaAsset) {
            requestFinished();
            return;
        }

        // setup
        if (this.#sourceType === Config.VIDEO_TYPE_BACKGROUND) {
            /**
             * CREATE VIDEO
             * NOTE: DO NOT mute before load, as Chrome bugs and creates a WebGL error
             *       https://bugs.chromium.org/p/chromium/issues/detail?id=898550
             *
             * Preloading:
             * The only reason we create the video element ourselves is to avoid PIXI setting preload="auto".
             * Preloading causes seeking to be super slow for some videos.
             * It requires more network and processing, especially with trimmed media, where
             * it seems that browsers try to load all the file instead of seeking.
             */
            const videoElement = document.createElement('video');
            videoElement.setAttribute('preload', 'none');
            videoElement.setAttribute('webkit-playsinline', '');
            videoElement.setAttribute('playsinline', '');

            PIXI.BaseImageResource.crossOrigin(videoElement, this.#sourceUrl);
            const sourceElement = document.createElement('source');
            sourceElement.src = this.#sourceUrl;
            sourceElement.type = this.#sourceMimeType;
            videoElement.appendChild(sourceElement);

            this.#resource = new VideoResource(videoElement, {
                autoLoad: false,
                autoPlay: false,
            });
            this.#resource.autoUpdate = false;
        }
        else if (this.#sourceType === Config.IMAGE_TYPE_BACKGROUND) {
            // CREATE IMAGE
            // setup resource
            this.#resource = new PIXI.ImageResource(this.#sourceUrl, { autoLoad: false });
        }
        else {
            throw Error(`Unknown data type "${this.#sourceType}". ${this.classType} only supports video or image.`);
        }

        this.#originalTexture = PIXI.Texture.from(this.#resource as any, this.#getTextureOptions());
        this.#sprite = new PIXI.Sprite(this.#originalTexture);

        if (this.textureSourceDomElement instanceof HTMLVideoElement) {
            this.#audioAsset.mediaElement = this.textureSourceDomElement;
        }

        // Control the loading of the resource, so we can gracefully catch any failures
        const resource = this.#resource;
        const sourceType = this.#sourceType;
        resource.load().then(() => {
            if (sourceType === Config.IMAGE_TYPE_BACKGROUND && !this.destroyed) {
                requestFinished();
                this.requiresRedraw = true;
            }
            else {
                requestFinished();
            }
        }).catch((_e: Event) => {
            this.#loadAttemptFailed('Media resource failed to load');
            requestFinished();
        });

        // add sprite to the container
        this.#dynamicAnimationContainer.removeChildren();
        this.#dynamicAnimationContainer.addChild(this.#sprite);

        if (this.isLoaded) {
            this.#emitSourceLoaded();
        }
        else {
            this.#sprite.texture.baseTexture.on('loaded', this.#emitSourceLoaded, this);
            this.#sprite.texture.baseTexture.on('error', this.#onBaseTextureError, this);
            const intervalTime = (this.isVideo) ?
                this.root.settings.video.LoadStatusInterval.current :
                this.root.settings.image.LoadStatusInterval.current;
            this.#loadStatusInterval = setTimeout(this._loadStatus, intervalTime);
        }
    }

    #getTextureOptions() {
        if (this.#shouldUseMipMapping) {
            return {
                mipmap: PIXI.MIPMAP_MODES.ON,
                scaleMode: PIXI.SCALE_MODES.LINEAR,
            };
        }
        return {};
    }

    /**
     * Returns true when the asset is ready for playback. This doesn't necessarily
     * mean that the asset is loaded. It just means that we've given it enough *tries*
     * and thus time to load and we now want to see the result. We do this to ensure
     * we can playback something after and x amount of tries.
     */
    override get canPlay() {
        if (this.destroyed) {
            return false;
        }
        let mediaAssetCanPlay = false;
        if (!this.shouldLoad) {
            mediaAssetCanPlay = true;
        }
        else {
            const max_retries = (this.isVideo) ?
                this.root.settings.video.MaxRetriesBeforeEnablingPlayback :
                this.root.settings.image.MaxRetriesBeforeEnablingPlayback;
            mediaAssetCanPlay = (max_retries < this.#loadStatusCount || this.isLoaded || this.#failed || this.sentCanPlay);
        }

        const audioAssetCanPlay = this.#audioAsset.canPlay;
        return mediaAssetCanPlay && audioAssetCanPlay;
    }

    /**
     * loadStatus verifies the load status of the resource after an x amount of time
     * and responds to ensure a representation of the asset gets loaded.
     * @internal
     */
    _loadStatus = () => {
        if (!this.isLoaded && !this.destroyed) {
            // retry loading the source
            this._updateSourceOnMediaTag();

            if (!this.isLoaded) {
                this.#loadStatusCount++;
                if (this.canPlay) {
                    this._emitAssetCanPlay();
                }
            }

            if (this.#loadStatusCount > this.#loadStatusMaxCount) {
                this.#emitLoadError();
            }
            else {
                // with a interval time of 2.5 sec we get the following retry times in seconds: [2.5, 4, 6, 11, 18, 30]
                const intervalTime = (this.isVideo) ?
                    this.root.settings.video.LoadStatusInterval.current :
                    this.root.settings.image.LoadStatusInterval.current;
                const newTimeout = intervalTime * (1.65 ** this.#loadStatusCount);
                this.#loadStatusInterval = setTimeout(this._loadStatus, newTimeout);
            }
        }
    };

    #onBaseTextureError(_baseTexture: PIXI.BaseTexture, _error: Event) {
        this.#loadAttemptFailed('BaseTexture failed to load');
    }

    onLoaded() {
        if (!this.isLoaded) {
            return;
        }
        this.#clearLoadStatusInterval();
        this.#clearBufferingTimeoutFallback();
        this.#loadStatusCount = 0;
        if (this.isVideo) {
            if (this.isLinkedToCompositingAsset) {
                this.#sourceDuration = this.#linkedMediaAsset.duration;
            }
            else {
                const videoElement = <HTMLVideoElement> this.textureSourceDomElement;
                if (!videoElement) {
                    throw Error('Video element not found');
                }
                // make sure the internal duration matches the loaded <video> duration
                if (videoElement.duration) {
                    const roundedDurationMS = roundToPrecision(videoElement.duration * 1000, Config.VIDEO_TIME_PRECISION_MS);
                    if (this.#sourceDuration !== roundedDurationMS) {
                        this.#sourceDuration = roundedDurationMS;
                    }
                }
            }
        }

        // make sure we redraw the object now that we loaded the media asset
        this.requiresRedraw = true;
    }

    /**
     * For the containers that we use for rotating and scaling, we want that to happen from the center of the media.
     * So this method adjusts the pivot & position of the containers to make sure that happens.
     */
    #centerPivotForScaleContainers() {
        if (!this.#sprite) {
            return;
        }
        const width = this.#sprite.width;
        const height = this.#sprite.height;
        const containersToCenter = [this._kenburnsScaleContainer, this.#scaleRotateContainer, this.#dynamicAnimationContainer];
        containersToCenter.forEach(container => {
            container.pivot.set(width / 2, height / 2);
            container.position.set(width / 2, height / 2);
        });
    }

    #applyFlip() {
        this.#scaleRotateContainer.scale.x = this.flipHorizontally ? -1 : 1;
        this.#scaleRotateContainer.scale.y = this.flipVertically ? -1 : 1;
    }

    #applyRotation() {
        this.#scaleRotateContainer.rotation = this.transformation.rotation;
    }

    /**
     * Returns true if we resized the image
     */
    get resizedImage(): boolean {
        return this.#sprite.texture !== this.#originalTexture;
    }


    /**
     * Resizes our source to match our media target resolution. Depending on the resizing mode, we either use mipmapping
     * or resize the image ourselves. Both have different visual quality, while mipmapping gives us a softer result,
     * resizing the image is sharper.
     */
    protected resizeMedia() {
        // make sure the source is valid
        if (!this.isSourceValid) {
            return;
        }

        // make sure the image is loaded
        if (this.mediaAssetType === 'image') {
            const image = <HTMLImageElement>(this.#originalTexture.baseTexture.resource as PIXI.ImageResource).source;
            if (!image.complete) {
                return;
            }
        }

        const origTransformation = resetTransform(this.#sprite);
        const origSourceResolution = { width: this.#originalTexture.width, height: this.#originalTexture.height };
        let { width, height, ratio } = calculateScaledResolution(origSourceResolution, this.#mediaTargetResolution, this.fillMode);

        /**
         * Scaling should only work for fill = cover:
         * https://lumen5.atlassian.net/browse/LU-2036
         */
        if (this.fillMode === Config.FILL_COVER) {
            width *= this.scaleX;
            height *= this.scaleY;
        }

        // specific logic for shrinking images -- here we re-render the actual media element at a lower resolution
        if (this.root._resizeImageAssets
            && !this.root._webGLContextLost
            && ratio < 1
            && this.mediaAssetType === 'image'
            && this.#shouldRenderResizedImage
        ) {
            // resize based on ratio
            const tempSprite = new PIXI.Sprite(this.#originalTexture);
            tempSprite.width = width;
            tempSprite.height = height;

            // make sure all of the necessary pieces are available
            if (!this.root || !this.root.renderer) { return; }

            // We scaled down, but in the reference coordinates. To scale to the output coordinate frame, we set the
            // resolution of the base texture, which will shrink down the real width and height.
            const renderTexture = PIXI.RenderTexture.create({ width, height, resolution: this.referenceToOutputMultiplier });
            this.root.renderer.resize(width, height);
            this.root.renderer.render(tempSprite, { renderTexture });
            this.root.renderer.resize(this.root.referenceResolution.width, this.root.referenceResolution.height);

            this.#sprite.texture = renderTexture;
            this.#dynamicAnimationContainer.removeChildren();
            this.#dynamicAnimationContainer.addChild(this.#sprite);
            if (this.#dynamicAnimationContainer.children.length !== 1) {
                throw Error('More then 1 child');
            }
            setTransform(this.#sprite, origTransformation, { scale: false });

            // cleanup
            tempSprite.destroy(false);
        }
        else {
            this.#sprite.width = width;
            this.#sprite.height = height;
            setTransform(this.#sprite, origTransformation, { scale: false });
        }
    }

    /**
     * The media asset could not load after all attempts, give up loading and emit an error.
     */
    #emitLoadError() {
        this.#failed = true;
        this.#clearLoadStatusInterval();
        this.#clearBufferingTimeoutFallback();
        this.#loadStatusCount = 0;

        this.emit('onLoadError', new Error('Media asset failed to load after max attempts'));

        // even though nothing visual has changed, communicate that our internal state has changed to failed.
        this.requiresRedraw = true;
    }

    /**
     * Clears the bufferingTimeoutFallback timeout. This timeout is setup a part of `bufferAtTime` to ensure our call
     * resolves within a certain amount of time.
     */
    #clearBufferingTimeoutFallback() {
        if (this.#onBufferingTimeoutFallback !== null) {
            clearTimeout(this.#onBufferingTimeoutFallback);
            this.#onBufferingTimeoutFallback = null;
        }
    }

    /**
     * Clears the loadStatus timeout which calls `_loadStatus` method. See `_loadStatus` for more details.
     */
    #clearLoadStatusInterval() {
        if (this.#loadStatusInterval !== null) {
            clearTimeout(this.#loadStatusInterval);
            this.#loadStatusInterval = null;
        }
    }

    /**
     * Useful for linking media so we can keep any applied transformations but reset the rest of
     * the media asset state.
     */
    #clearExceptTransformations() {
        // remove buffering and load timeouts
        this.#clearBufferingTimeoutFallback();
        this.#clearLoadStatusInterval();
        this.#stopUsingCachedTexture();
        this.#failed = false;
        this.#loadStatusCount = 0;
        this.sentCanPlay = false;
        this.#loadingSourceUrl = null;
        const isNotLinkedToOtherAsset = this.#linkedMediaAsset === null;

        // remove resource listeners
        if (this.#sprite) {
            this.#sprite.texture?.baseTexture?.off('loaded', this.#emitSourceLoaded, this);
            this.#sprite.texture?.baseTexture?.off('error', this.#onBaseTextureError, this);
            if (this.#sprite.texture?.baseTexture?.hasOwnProperty('source') && this.textureSourceDomElement) {
                this.textureSourceDomElement.removeEventListener('canplay', this._onSourceBufferingDone);
                this.textureSourceDomElement.removeEventListener('canplaythrough', this._onSourceBufferingDone);
                this.textureSourceDomElement.removeEventListener('seeked', this._onSourceBufferingDone);
            }
            this.#sprite.destroy({ baseTexture: isNotLinkedToOtherAsset, texture: isNotLinkedToOtherAsset });
        }
        // if we resized the image then we'll have a render texture so we'll have to manually delete the original texture
        if (this.#originalTexture?.baseTexture && !this.#originalTexture.baseTexture.destroyed) {
            this.#originalTexture.destroy(isNotLinkedToOtherAsset);
        }

        if (isNotLinkedToOtherAsset && this.#resource && !this.#resource.destroyed) {
            this.#resource.destroy();
        }

        this.#linkedMediaAsset = null;
        this.#sprite = null;
        this.#originalTexture = null;
        this.#resource = null;
    }

    /**
     * Clears our object so we can load a source through `#loadSource`.
     */
    override clear() {
        // reset transformation
        resetTransform(this);

        // reset transform matrix
        this.resetTransformMatrix();

        this.#clearExceptTransformations();
    }

    /**
     * Destroy the object. The `MediaAsset` can no longer be used once it's destroyed.
     * @param options
     */
    override destroy(options?: DestroyOptions) {
        this.#abortBuffering(new BufferingAbortedError(this.classType, this._luminaryParent?.index, 'destroy called'));
        this.clear();
        super.destroy(options);

        this.#audioAsset.destroy();
    }

    /**
     * Our currently active source url
     */
    get sourceUrl(): string {
        return this.#sourceUrl;
    }

    /**
     * Check if `media` is explicitly disabled in fetchResources
     */
    override get shouldLoad(): boolean {
        if (typeof this.fetchResources === 'boolean') {
            return super.shouldLoad;
        }

        if (this.fetchResources.media === FETCH_ON_BUFFER) {
            if (this.#linkedMediaAsset) {
                return this.#linkedMediaAsset.shouldLoad;
            }

            if (this.#loadingSourceUrl) {
                // We have already started loading the source, which means we did request
                // to load the media already.
                return true;
            }
            return false;
        }

        return this.fetchResources && this.fetchResources.media;
    }

    override get failedToLoad() {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.failedToLoad;
        }

        return this.#failed;
    }

    get isSourceValid() {
        if (!this.#sprite) {
            return false;
        }
        if (!this.#sprite.texture) {
            return false;
        }
        if (!this.#sprite.texture.baseTexture) {
            return false;
        }
        if (this.#linkedMediaAsset) {
            return true;
        }
        if (!this.#resource) {
            return false;
        }
        if (!this.#resource.source) {
            return false;
        }
        return this.#resource.valid;
    }

    override get isLoaded() {
        if (!this.#loadingSourceUrl && this.skipBuildAndDraw) {
            return true;
        }
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.isLoaded;
        }
        if (!this.sourceUrl) {
            return true;
        }
        if (!this.isSourceValid) {
            return false;
        }
        if (!this._luminaryParent || this._luminaryParent?.destroyed) {
            return false;
        }
        if (this.isVideo) {
            // just checking for duration might be a better way to determine to whether
            // or not the asset is loaded since we've seen some cases in where we got a
            // duration but never valid = true.
            if (!this.textureSourceDomElement) {
                return false;
            }

            if (this.failedToLoad) {
                // This checking definitely make sense for NodeVideoElement.
                // error event can happen during loading assets before calling `bufferAtTime` method
                // TODO: should we return false always when this.failedToLoad for all cases?
                return false;
            }

            if (!Number.isNaN((this.textureSourceDomElement as HTMLVideoElement).duration)
                && (this.textureSourceDomElement as HTMLVideoElement).duration !== undefined
                && this.#sprite.texture.baseTexture.valid === true
            ) {
                return true;
            }
        }
        else {
            return this.#sprite.texture.baseTexture.valid;
        }
        return false;
    }

    get isVideo(): boolean {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.isVideo;
        }
        return this.#sourceType === Config.VIDEO_TYPE_BACKGROUND;
    }

    /**
     * get the dom element for the texture source
     */
    get textureSourceDomElement(): HTMLVideoElement | HTMLImageElement {
        if (this.#originalTexture
            && this.#originalTexture.baseTexture
            && this.#originalTexture.baseTexture.resource) {
            return (this.#originalTexture.baseTexture.resource as PIXI.BaseImageResource).source as any;
        }
    }

    override _propagatePlay() {
        if (this.#linkedMediaAsset) {
            return;
        }
        this.#stopUsingCachedTexture();
        if (this.isVideo
            && this.isSourceValid
            && this.textureSourceDomElement
            && typeof (this.textureSourceDomElement as HTMLVideoElement).play === 'function'
        ) {
            const playPromise = (this.textureSourceDomElement as HTMLVideoElement).play();

            if (playPromise instanceof Promise) {
                playPromise.catch((e: DOMException) => {
                    // ignore AbortError which happens when play interrupted by calling pause.
                    // https://developer.chrome.com/blog/play-request-was-interrupted/
                    if (e.name === 'AbortError') {
                        return;
                    }
                    // we should wrap native error explicitly in order to extend error with callstack
                    throw new Error(e.message);
                });
            }
            else {
                // for some reason sometimes video element does not return a promise
                // so we need to notify about this case
                Sentry.captureMessage('play method does not return a promise', 'warning');
            }
        }
        this.#audioAsset._propagatePlay();
    }

    override _propagatePause() {
        if (this.#linkedMediaAsset) {
            return;
        }
        if (this.isVideo
            && this.isSourceValid
            && this.textureSourceDomElement
            && typeof (this.textureSourceDomElement as HTMLVideoElement).pause === 'function'
        ) {
            (this.textureSourceDomElement as HTMLVideoElement).pause();
        }
        this.#audioAsset._propagatePause();
    }

    /**
     * @internal
     */
    get _isUsingCachedTexture() {
        if (!this.isVideo) {
            return false;
        }
        return this.#sprite.texture !== this.#originalTexture;
    }

    /**
     * Tries to use the cached texture for the current time.
     */
    #useCachedTextureIfAvailable(time: number) {
        if (this.destroyed) {
            return;
        }
        if (!this.isVideo) {
            return;
        }
        const videoTime = this.timeRelativeTo(time);
        const cachedTexture = this.root.globalMediaTextureCache.get(this.#sourceUrl, videoTime);
        if (cachedTexture && cachedTexture.valid) {
            this.#sprite.texture = cachedTexture;
        }
    }

    #stopUsingCachedTexture() {
        if (this.destroyed) {
            return;
        }
        if (!this.#sprite) {
            return;
        }
        this.#sprite.texture = this.#originalTexture;
    }

    _updateMediaTexture() {
        if (this.destroyed) {
            return;
        }

        // If the old film filter is used, update the seed to animate the noise
        const oldFilmFilter = this.#getOldFilmFilter();
        if (oldFilmFilter) {
            oldFilmFilter.seed = Math.random();
        }

        if (!this.isVideo) {
            return;
        }

        // make sure the source still exists before trying to update it
        if (!this.isSourceValid) {
            return;
        }

        if (this.#linkedMediaAsset) {
            // Update the sprite's texture to the current linked asset's texture.
            // This is required when buffering or updating the texture in general, since the original media can switch
            // textures (e.g. when using the global media texture cache).
            this.#sprite.texture = this.#linkedMediaAsset._displayTexture;
            return;
        }

        // The cached texture is a single image (a video frame) so we don't need to update it
        if (this._isUsingCachedTexture) {
            return;
        }

        try {
            this.#sprite.texture.update();
        }
        catch (err) {
            console.warn(err);
        }
    }

    /**
     * Calculate the target resolution for our media source.
     */
    #calculateMediaTargetResolution() {
        let width = this.maxWidth;
        let height = this.maxHeight;

        if (this.mask instanceof StencilMask) {
            ({ width, height } = this.mask.getMaxBounds());
        }
        width *= this.scaleMultiplier;
        height *= this.scaleMultiplier;
        return { width, height };
    }

    /**
     * Calculate the position for our MediaAsset so we can place it in the center of the visible area. Here the visible area
     * is determined by the slide and whether our media is affect by a mask.
     */
    #calculateCenterOffset() {
        // position the media asset in the middle of the mask
        const objectWidth = this.#sprite.width;
        const objectHeight = this.#sprite.height;

        if (this.mask instanceof StencilMask) {
            const maxBounds = this.mask.getMaxBounds();
            const midPoint = getMidPointFromRect(maxBounds);
            const x = midPoint.x - (objectWidth / 2);
            const y = midPoint.y - (objectHeight / 2);
            return { x, y };
        }

        // TODO: replace with maxWidth and maxHeight?
        const x = -(objectWidth - this.root.referenceResolution.width) / 2;
        const y = -(objectHeight - this.root.referenceResolution.height) / 2;
        return { x, y };
    }

    /**
     * Calculates viewport resolution.
     */
    #getViewportResolution() {
        let viewportResolutionX = 0;
        let viewportResolutionY = 0;
        if (this.mask instanceof StencilMask) {
            const maxBounds = this.mask.getMaxBounds();
            viewportResolutionX = maxBounds.width;
            viewportResolutionY = maxBounds.height;
        }
        else {
            // TODO: replace with maxWidth and maxHeight?
            viewportResolutionX = this.root.referenceResolution.width;
            viewportResolutionY = this.root.referenceResolution.height;
        }
        return { viewportResolutionX, viewportResolutionY };
    }

    /**
     * Calculates relative and absolute limits for translation.
     */
    getTranslationLimit(): TranslationLimit {
        const objectWidth = this.width;
        const objectHeight = this.height;
        const { viewportResolutionX, viewportResolutionY } = this.#getViewportResolution();
        const maxOffsetX = Math.abs((objectWidth - viewportResolutionX) / 2);
        const maxOffsetY = Math.abs((objectHeight - viewportResolutionY) / 2);
        const tx = parseFloat((maxOffsetX / viewportResolutionX).toFixed(2));
        const ty = parseFloat((maxOffsetY / viewportResolutionY).toFixed(2));
        return {
            x: [-1 * tx, +1 * tx],
            y: [-1 * ty, +1 * ty],
        };
    }

    getScaleLimit(): { x: [number, number]; y: [number, number]; } {
        return {
            x: [Config.MEDIA_MIN_SCALE_X, Config.MEDIA_MAX_SCALE_X],
            y: [Config.MEDIA_MIN_SCALE_Y, Config.MEDIA_MAX_SCALE_Y],
        };
    }

    /**
     * Ensure our position or cropping values completely fill the visible area. If that's not the case, then we clamp
     * them here.
     */
    #calculateCropTranslation() {
        const objectWidth = this.#sprite.width;
        const objectHeight = this.#sprite.height;

        const { viewportResolutionX, viewportResolutionY } = this.#getViewportResolution();

        // TODO: use _transformation object instead? This would remove the need for transformMatrix
        const maxXOffset = Math.abs((objectWidth - viewportResolutionX) / 2);
        const userXOffset = Math.abs(this.#transformMatrix.tx * viewportResolutionX);
        const offsetX = Math.min(maxXOffset, userXOffset);
        const multiX = (this.#transformMatrix.tx < 0) ? -1 : 1;

        // y
        const maxYOffset = Math.abs((objectHeight - viewportResolutionY) / 2);
        const userYOffset = Math.abs(this.#transformMatrix.ty * viewportResolutionY);
        const offsetY = Math.min(maxYOffset, userYOffset);
        const multiY = (this.#transformMatrix.ty < 0) ? -1 : 1;

        return {
            x: offsetX * multiX,
            y: offsetY * multiY,
        };
    }

    /**
     * Set the position of the MediaAsset
     */
    override applyTransformation(_offsetX = 0, _offsetY = 0) {
        if (!this.#sprite) {
            return;
        }

        // If a position transformation hasn't been applied, we automatically center the media
        let centerOffset = { x: 0, y: 0 };
        if (this.transformation.position.x === 0 && this.transformation.position.y === 0) {
            centerOffset = this.#calculateCenterOffset();
        }

        const cropOffset = this.#calculateCropTranslation();

        // we want to apply rotation around the center of the media asset,
        // so negate it on the base asset and we'll handle it here in Media Asset
        const rotationOffset = -this.transformation.rotation;

        // apply new position
        const offsetX = centerOffset.x - cropOffset.x;
        const offsetY = centerOffset.y - cropOffset.y;
        super.applyTransformation(offsetX, offsetY, rotationOffset);

        this.#centerPivotForScaleContainers();
        this.#applyFlip();
        this.#applyRotation();
    }

    /**
     * Applies any necessary animation presets and configurations
     */
    override applyAnimation() {
        // The old system used animationConfig, which is applied by the container.
        // We don't support both animationConfig and dynamic style, but kenburn animation needs to be applied
        // regardless.
        if (!this.hasDynamicStyle) {
            super.applyAnimation();
            this._applyKenBurn();
        }
        else if (this.animationConfig.length === 0) {
            this._applyKenBurn();
        }
    }

    override _checkAndFixProperties() {
        super._checkAndFixProperties();
        // Ensure that kenburns is set to NONE if this is a video asset
        // because kenburns are only supported for images, not videos!
        if (this.isVideo && this.kenburn !== Config.NONE_BACKGROUND_ANIMATION) {
            this.kenburn = Config.NONE_BACKGROUND_ANIMATION;
        }
        this.#mediaTargetResolution = this.#calculateMediaTargetResolution();

        // Some transitions move the scene, and the mask must move with.
        if (this.mask && !(this.mask as any).parent) {
            (this.mask as any).parent = this.slide.scene;
        }
    }

    /**
     * Reset transform matrix when source data is changed.
     */
    resetTransformMatrix() {
        this.transformMatrix.reset();
    }

    /**
     * Reset the persisted values of the transformations that users can apply.
     */
    resetEditableTransformations() {
        this.flipHorizontally = false;
        this.flipVertically = false;
        this.transformation.rotation = 0;
        this.scaleX = 1;
        this.scaleY = 1;
        this.xTranslation = 0;
        this.yTranslation = 0;
    }

    _applyKenBurn() {
        const translateX = this.x;
        const translateY = this.y;

        const { viewportResolutionX, viewportResolutionY } = this.#getViewportResolution();
        const kenBurnsPanMovement = {
            x: viewportResolutionX * Config.KENBURN_PAN_PERCENTAGE / 2,
            y: viewportResolutionY * Config.KENBURN_PAN_PERCENTAGE / 2,
        };

        // reset the scale container, so if kenburns is changed, we clear the previous kenburns' scale
        this._kenburnsScaleContainer.scale.set(1);

        switch (this.#kenburn) {
            case Config.PAN_LEFT_BACKGROUND_ANIMATION: {
                const scaleFactor = 1 + Config.KENBURN_PAN_PERCENTAGE;
                this._kenburnsScaleContainer.scale.set(scaleFactor);
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [translateX - kenBurnsPanMovement.x, translateX + kenBurnsPanMovement.x],
                    ],
                    drivenObjects: [this],
                    drivenProperty: ['x'],
                });
                break;
            }
            case Config.PAN_RIGHT_BACKGROUND_ANIMATION: {
                const scaleFactor = 1 + Config.KENBURN_PAN_PERCENTAGE;
                this._kenburnsScaleContainer.scale.set(scaleFactor);
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [translateX + kenBurnsPanMovement.x, translateX - kenBurnsPanMovement.x],
                    ],
                    drivenObjects: [this],
                    drivenProperty: ['x'],
                });
                break;
            }
            case Config.PAN_DOWN_BACKGROUND_ANIMATION: {
                const scaleFactor = 1 + Config.KENBURN_PAN_PERCENTAGE;
                this._kenburnsScaleContainer.scale.set(scaleFactor);
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [translateY + kenBurnsPanMovement.y, translateY - kenBurnsPanMovement.y],
                    ],
                    drivenObjects: [this],
                    drivenProperty: ['y'],
                });
                break;
            }
            case Config.PAN_UP_BACKGROUND_ANIMATION: {
                const scaleFactor = 1 + Config.KENBURN_PAN_PERCENTAGE;
                this._kenburnsScaleContainer.scale.set(scaleFactor);
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [translateY - kenBurnsPanMovement.y, translateY + kenBurnsPanMovement.y],
                    ],
                    drivenObjects: [this],
                    drivenProperty: ['y'],
                });
                break;
            }
            case Config.ZOOM_IN_BACKGROUND_ANIMATION: {
                const scaleFactor = (1 + Config.KENBURN_ZOOM_PERCENTAGE);
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [1, scaleFactor],
                        [1, scaleFactor],
                    ],
                    drivenObjects: [this],
                    drivenProperty: ['_kenburnsScaleContainer.scale.x', '_kenburnsScaleContainer.scale.y'],
                });
                break;
            }
            case Config.ZOOM_OUT_BACKGROUND_ANIMATION: {
                const scaleFactor = (1 + Config.KENBURN_ZOOM_PERCENTAGE);
                this.animationManager.add({
                    name: `kenburn_${this.#kenburn}`,
                    mode: 'single',
                    curve: LinearCurve.raisingDescription(),
                    drivenRange: [
                        [scaleFactor, 1],
                        [scaleFactor, 1],
                    ],
                    drivenObjects: [this],
                    drivenProperty: ['_kenburnsScaleContainer.scale.x', '_kenburnsScaleContainer.scale.y'],
                });
                break;
            }

            case Config.NONE_BACKGROUND_ANIMATION:
                break;

            default:
                throw Error(`Invalid key burn option ${this.#kenburn}`);
        }
    }

    /**
     * Return the current time in seconds
     */
    get currentTime(): number {
        if (this.isVideo && this.isSourceValid) {
            return (this.textureSourceDomElement as HTMLVideoElement).currentTime;
        }
        return 0;
    }

    timeRelativeTo(time: number): number {
        return time;
    }

    /**
     * Buffer data at the given time and resolve the moment we have enough data to enable the given mode (single or play).
     * Single mode is mainly being use when rendering images while play is used to enable playback.
     */
    override async _bufferAtTime(time: number, bufferingMode: BufferingMode): Promise<void> {
        if (typeof this.fetchResources !== 'boolean' && this.fetchResources?.media === FETCH_ON_BUFFER) {
            // At this point, since we requested buffering, we now should load media for this asset
            this._loadSourceWithNetworkManager(true);
        }

        try {
            await this.onSourceLoaded();

            // check for cases where buffering isn't possible
            if (!this.isVideo || this.failedToLoad || !this.shouldLoad || !this.visible) {
                return;
            }

            // if the time is for before this asset, just buffer at zero
            time = Math.max(time, 0);
            await this.#bufferAtTime(time, bufferingMode);
            this.#audioAsset._bufferAtTime(time, bufferingMode);
        }
        catch (e) {
            if (e instanceof BufferingAbortedError) {
                // Don't propagate an error, but rather just resolve
                return;
            }
            throw e;
        }
    }

    async onSourceLoaded() {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.onSourceLoaded();
        }
        if (this.isDoneTryingToLoad || this.destroyed) {
            return;
        }
        return new Promise((resolve, reject) => {
            this.once('onLoaded', resolve);
            this.once('onLoadAttemptFailed', (error: Error) => {
                reject(new FailedToLoadError('MediaAsset failed to load.', {
                    error,
                    sourceData: this.sourceData,
                    readyState: (this.isVideo && this.isLinkedToMediaAsset)
                        ? (this.textureSourceDomElement as HTMLVideoElement)?.readyState
                        : null,
                }));
            });
            this.#abortBuffering = reject;
        });
    }

    async #bufferAtTime(time: number, bufferingMode: BufferingMode): Promise<void> {
        if (this.#linkedMediaAsset) {
            // Let linked media get buffer.
            return;
        }
        this.#stopUsingCachedTexture();
        if (bufferingMode === BufferingMode.ResolveWhenSingleFrame) {
            this.#useCachedTextureIfAvailable(time);
            if (this._isUsingCachedTexture) {
                // we have this texture cached, we don't need to wait for it.
                return;
            }
        }

        const waitForState = (bufferingMode === BufferingMode.ResolveWhenCanPlay) ? HTMLVideoElement.prototype.HAVE_ENOUGH_DATA : HTMLVideoElement.prototype.HAVE_CURRENT_DATA;
        const textureSourceDomElement = <HTMLVideoElement> this.textureSourceDomElement;
        const isVideo = this.isVideo;

        const _isVideoSourceDoneBuffering = () => {
            // In firefox, the readyState changes asynchronously, so we also need
            // to check to see if it's seeking
            if (!isVideo) {
                return true;
            }
            return (textureSourceDomElement.readyState >= waitForState
                && textureSourceDomElement.seeking === false);
        };

        return new Promise((resolve, reject) => {
            this.#abortBuffering = reject;
            const bufferingDoneListener = () => {
                if (!textureSourceDomElement || this.destroyed) {
                    reject(new Error('Source is null'));
                    return;
                }
                // wait until the source tag is ready and is no longer seeking.
                if (_isVideoSourceDoneBuffering()) {
                    textureSourceDomElement.removeEventListener('canplay', this._onSourceBufferingDone);
                    textureSourceDomElement.removeEventListener('canplaythrough', this._onSourceBufferingDone);
                    textureSourceDomElement.removeEventListener('seeked', this._onSourceBufferingDone);

                    this.#clearBufferingTimeoutFallback();
                    this.off('onBufferingDone', bufferingDoneListener);

                    if (bufferingMode === BufferingMode.ResolveWhenSingleFrame) {
                        /**
                         * The texture cache didn't have an entry, so we need to create a new
                         * texture from the video DOM element at this point in time.
                         */
                        this.#cacheTextureAtTime(time, textureSourceDomElement);
                    }
                    resolve();
                }
            };
            this.on('onBufferingDone', bufferingDoneListener);

            if (!textureSourceDomElement) {
                reject(new Error('textureSourceDomElement is null'));
                return;
            }
            textureSourceDomElement.addEventListener('canplay', this._onSourceBufferingDone);
            textureSourceDomElement.addEventListener('canplaythrough', this._onSourceBufferingDone);
            textureSourceDomElement.addEventListener('seeked', this._onSourceBufferingDone);

            // when playback rate is below 0.075 we'll get a HTMLMediaElement
            // error -- this catches this error
            try {
                const relTime = this.timeRelativeTo(time);
                if (isNaN(relTime)) {
                    throw Error(`Relative time is NaN for ${time}`);
                }
                textureSourceDomElement.currentTime = relTime;
            }
            catch (err) {
                console.warn(err);
                this.#clearBufferingTimeoutFallback();
                reject(err);
            }

            if (_isVideoSourceDoneBuffering()) {
                this.emit('onBufferingDone');
            }
            else {
                // maximum wait timeout fallback
                this.#clearBufferingTimeoutFallback();
                const seekWaitTime = this.root.settings.video.seekWaitTime;
                this.#onBufferingTimeoutFallback = setTimeout(() => {
                    textureSourceDomElement.removeEventListener('canplay', this._onSourceBufferingDone);
                    textureSourceDomElement.removeEventListener('canplaythrough', this._onSourceBufferingDone);
                    textureSourceDomElement.removeEventListener('seeked', this._onSourceBufferingDone);

                    if (!_isVideoSourceDoneBuffering()) {
                        // if it's still seeking, let's listen for the seeked event and then trigger a redraw
                        if (textureSourceDomElement.seeking) {
                            textureSourceDomElement.addEventListener('seeked', () => {
                                this.requiresRedraw = true;
                            }, { once: true });
                        }
                        else {
                            console.warn(`Seek Timeout after ${seekWaitTime}ms = {readyState: ${textureSourceDomElement.readyState}, seeking: ${textureSourceDomElement.seeking}}, expecting {readyState: ${waitForState}, seeking: false}`);
                        }
                    }
                    resolve();
                }, seekWaitTime);
            }
        });
    }

    /**
     * We take an image snapshot of the video's current frame and store
     * that as a new texture in our media texture cache.
     */
    #cacheTextureAtTime(time: number, textureSourceDomElement: HTMLVideoElement) {
        if (this.#textureCacheHasValidEntry(time)) {
            return;
        }

        if (global && global.NODE) {
            /**
             * We have to skip populating the cache in node because the canvas API in node does
             * not support ctx.drawImage(video). We could do something like:
             *
             * const tmp = PIXI.Sprite.from(this.#originalTexture);
             * let array = this.root.renderer.extract.pixels(tmp);
             *
             * or using a render texture:
             * https://github.com/Lumen5/luminary/blob/cf2c519b1b2d80d8c3dc145d943fb54f5e2e7ec4/src/core/asset/MediaAsset.ts#L1770
             *
             * but this is much slower and we don't actually need this use case in node (for now)
             */
            return;
        }


        const canvas = document.createElement('canvas');
        canvas.width = textureSourceDomElement.videoWidth;
        canvas.height = textureSourceDomElement.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(textureSourceDomElement, 0, 0, canvas.width, canvas.height);
        const resource = new PIXI.CanvasResource(canvas);
        const texture = PIXI.Texture.from(resource as any, this.#getTextureOptions());
        this.root.globalMediaTextureCache.set(this.#sourceUrl, this.timeRelativeTo(time), texture);
    }

    #textureCacheHasValidEntry(time: number) {
        const texture = this.root.globalMediaTextureCache.get(this.#sourceUrl, this.timeRelativeTo(time));
        return texture && texture.valid;
    }

    set brightness(value: number) {
        const filter = this.#getOrCreateAdjustmentFilter();
        filter.brightness = value;
    }

    get brightness() {
        const filter = this.#getAdjustmentFilter();

        if (!filter) {
            return 1.0;
        }

        return filter.brightness;
    }

    set contrast(value: number) {
        const filter = this.#getOrCreateAdjustmentFilter();
        filter.contrast = value;
    }

    get contrast() {
        const filter = this.#getAdjustmentFilter();

        if (!filter) {
            return 1.0;
        }

        return filter.contrast;
    }

    #getAdjustmentFilter(): AdjustmentFilter | undefined {
        return this.getFilterByName(MediaAsset.ADJUSTMENT_FILTER_NAME) as AdjustmentFilter;
    }

    #getOrCreateAdjustmentFilter(): AdjustmentFilter {
        let filter = this.#getAdjustmentFilter();

        if (!filter) {
            filter = this.addFilter(Config.ADJUSTMENT_FILTER, MediaAsset.ADJUSTMENT_FILTER_NAME);
        }

        return filter;
    }

    set hue(value: number) {
        const filter = this.#getOrCreateHslAdjustmentFilter();
        filter.hue = value;
    }

    get hue() {
        const filter = this.#getHslAdjustmentFilter();

        if (!filter) {
            return 0;
        }

        return filter.hue;
    }

    set lightness(value: number) {
        const filter = this.#getOrCreateHslAdjustmentFilter();
        filter.lightness = value;
    }

    get lightness() {
        const filter = this.#getHslAdjustmentFilter();

        if (!filter) {
            return 0;
        }

        return filter.lightness;
    }

    set saturation(value: number) {
        const filter = this.#getOrCreateHslAdjustmentFilter();
        filter.saturation = value;
    }

    get saturation() {
        const filter = this.#getHslAdjustmentFilter();

        if (!filter) {
            return 0;
        }

        return filter.saturation;
    }

    #getHslAdjustmentFilter(): HslAdjustmentFilter | undefined {
        return this.getFilterByName(MediaAsset.HSL_ADJUSTMENT_FILTER_NAME) as HslAdjustmentFilter;
    }

    #getOrCreateHslAdjustmentFilter(): HslAdjustmentFilter {
        let filter = this.#getHslAdjustmentFilter();

        if (!filter) {
            filter = this.addFilter(Config.HSL_ADJUSTMENT_FILTER, MediaAsset.HSL_ADJUSTMENT_FILTER_NAME);
        }

        return filter;
    }

    set blur(value: number) {
        let filter = this.getFilterByName(MediaAsset.BLUR_FILTER_NAME) as BlurFilter;

        if (!filter) {
            filter = this.addFilter(Config.BLUR_FILTER, MediaAsset.BLUR_FILTER_NAME);
        }

        filter.blur = value;
    }

    get blur(): number {
        const filter = this.getFilterByName(MediaAsset.BLUR_FILTER_NAME) as BlurFilter;

        if (!filter) {
            return 0;
        }

        return filter.blur;
    }

    set noise(value: number) {
        const filter = this.#getOrCreateOldFilmFilter();
        filter.noise = value;
    }

    get noise() {
        const filter = this.#getOldFilmFilter();

        if (!filter) {
            return 0;
        }

        return filter.noise;
    }

    set noiseSize(value: number) {
        const filter = this.#getOrCreateOldFilmFilter();
        filter.noiseSize = value;
    }

    get noiseSize() {
        const filter = this.#getOldFilmFilter();

        if (!filter) {
            return 1;
        }

        return filter.noiseSize;
    }

    set vignetting(value: number) {
        const filter = this.#getOrCreateOldFilmFilter();
        filter.vignetting = value;
    }

    get vignetting() {
        const filter = this.#getOldFilmFilter();

        if (!filter) {
            return 0;
        }

        return filter.vignetting;
    }

    set vignettingAlpha(value: number) {
        const filter = this.#getOrCreateOldFilmFilter();
        filter.vignettingAlpha = value;
    }

    get vignettingAlpha() {
        const filter = this.#getOldFilmFilter();

        if (!filter) {
            return 1;
        }

        return filter.vignettingAlpha;
    }

    set vignettingBlur(value: number) {
        const filter = this.#getOrCreateOldFilmFilter();
        filter.vignettingBlur = value;
    }

    get vignettingBlur() {
        const filter = this.#getOldFilmFilter();

        if (!filter) {
            return 0;
        }

        return filter.vignettingBlur;
    }

    #getOldFilmFilter(): OldFilmFilter | undefined {
        return this.getFilterByName(MediaAsset.OLD_FILM_FILTER_NAME) as OldFilmFilter;
    }

    #getOrCreateOldFilmFilter(): OldFilmFilter {
        let filter = this.#getOldFilmFilter();

        if (!filter) {
            filter = this.addFilter(Config.OLD_FILM_FILTER, MediaAsset.OLD_FILM_FILTER_NAME);
            filter.seed = Math.random();
        }

        return filter;
    }

    set enableGreyscale(value: boolean) {
        const filter = this.getFilterByName(MediaAsset.GREYSCALE_FILTER_NAME);
        if (value && !filter) {
            this.addFilter(Config.GREYSCALE_FILTER, MediaAsset.GREYSCALE_FILTER_NAME);
        }
        else if (!value && filter) {
            this.removeFilter(MediaAsset.GREYSCALE_FILTER_NAME);
        }
    }

    get enableGreyscale(): boolean {
        return this.getFilterByName(MediaAsset.GREYSCALE_FILTER_NAME) !== undefined;
    }

    /**
     * Exposes the filters which are not managed by luminary automatically. Some filters should only be used internally,
     * to provide functionality for MediaAsset, so outside of luminary we want to filter those out.
     */
    get externalFilters() {
        return this.getFilters().filter(f =>
            f.name !== MediaAsset.ADJUSTMENT_FILTER_NAME
            && f.name !== MediaAsset.BLUR_FILTER_NAME
            && f.name !== MediaAsset.GREYSCALE_FILTER_NAME
            && f.name !== MediaAsset.HSL_ADJUSTMENT_FILTER_NAME
            && f.name !== MediaAsset.OLD_FILM_FILTER_NAME
        );
    }

    set opacity(value: number) {
        if (value !== this.alpha) {
            this.alpha = value;
            this.requiresRedraw = true;
        }
    }

    get opacity() {
        return this.alpha;
    }

    get hasAudio(): boolean | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.hasAudio;
        }
        return this.#hasAudio;
    }

    get hasVoiceover(): boolean | null {
        if (this.#linkedMediaAsset) {
            return this.#linkedMediaAsset.hasVoiceover;
        }
        return this.#hasVoiceover;
    }

    get transformMatrix(): TransformMatrix {
        return this.#transformMatrix;
    }

    set scaleX(value: number) {
        this.transformMatrix.scaleX = value;
    }

    get scaleX() {
        return this.transformMatrix.scaleX;
    }

    set scaleY(value: number) {
        this.transformMatrix.scaleY = value;
    }

    get scaleY() {
        return this.transformMatrix.scaleY;
    }

    // TODO would be nice to refactor the API to use mediaAsset.transform.x/yTranslation
    set xTranslation(value) {
        this.#transformMatrix.xTranslation = value;
    }

    get xTranslation() {
        return this.#transformMatrix.xTranslation;
    }

    set yTranslation(value) {
        this.#transformMatrix.yTranslation = value;
    }

    get yTranslation() {
        return this.#transformMatrix.yTranslation;
    }

    get usesInvisibleURL() {
        return this.thumbnailUrl === Config.INVISIBLE_IMAGE
            && this.previewUrl === Config.INVISIBLE_IMAGE
            && this.renderUrl === Config.INVISIBLE_IMAGE;
    }

    override get propertiesToSerialize() {
        const properties = [
            'mask',
            'fillMode',
            'kenburn',
            'maxHeight',
            'maxWidth',
            'opacity',
            'scaleMultiplier',
            'transformMatrix',
            'shouldRenderResizedImage',
            'shouldUseMipMapping',
        ];
        return super.propertiesToSerialize
            .concat(getAttributesToSerialize(this))
            .concat(properties);
    }

    /**
     * List of element properties that can be edited by users and would need to be preserved
     * between aspect ratio changes.
     */
    override get editableProperties() {
        return super.editableProperties.concat([
            'sourceData',
        ]);
    }

    /**
     * @inheritDoc
     */
    override getCreationContainersAtLevelForType(level: DynamicStylingCreationLevel, type = CreationType.Animation): PIXI.Container[] {
        assertInEnum(type, CreationType);
        switch (level) {
            case DynamicStylingCreationLevel.Object:
                // The container used for animation is below the container where we add dynamic graphics to. This means
                // that animations are not applied to the dynamic graphics and let us only control the media asset itself.
                // This is important for masks -- if we want to reveal the media by moving it out from under a mask, we
                // don't want the mask to move with it. Animations for dynamic graphics can be set up on the graphic itself.
                return (type === CreationType.Animation) ? [this.#dynamicAnimationContainer] : [this.#scaleRotateContainer];
            default:
                console.warn(`Unsupported container of type ${level}`);
                return [];
        }
    }

    /**
     * get data object containing the base 64 string of the media asset including format and dimensions. This is handy
     * when the media asset is a linked media asset and thus doesn't have a URL to fetch the data directly from for
     * display.
     */
    async getBase64Data(): Promise<MediaAssetBase64Data> {
        // set the renderer resolution to 1 to get the correct base64 data
        this.root.renderer.resolution = 1;

        const format = 'image/png';
        const base64 = await this.root.renderer.extract.base64(this.#sprite, format, 1);

        // reset the renderer to its original resolution
        this.root.renderer.resolution = this.referenceToOutputMultiplier;

        return new MediaAssetBase64Data({
            base64,
            width: this._originalTexture.width,
            height: this._originalTexture.height,
            format,
        });
    }
}
